import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineDatabase, HiOutlineRefresh, HiOutlineExclamation, HiOutlineUpload } from 'react-icons/hi';
import { BackupsSkeleton } from '../components/Skeleton';

const API_BASE = process.env.REACT_APP_API_URL || '';
const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function formatBackupName(filename) {
  const match = filename.match(/ds5_(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})/);
  if (!match) return filename;
  const [, y, mo, d, h, mi] = match;
  return `${parseInt(d)} ${MONTHS_FR[parseInt(mo) - 1]} ${y} à ${h}:${mi}`;
}

export default function BackupsPage({ token }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState('');
  const [message, setMessage] = useState(null);
  // Modal state for restore confirmation
  const [restoreModal, setRestoreModal] = useState(null); // { filename, step: 'confirm'|'pin' }
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBackups = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/monitoring/backups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBackups(data.backups || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const openRestoreModal = (filename) => {
    setRestoreModal({ filename, step: 'confirm' });
    setPinValue('');
    setPinError('');
  };

  const handleConfirmStep = () => {
    setRestoreModal(prev => ({ ...prev, step: 'pin' }));
  };

  const handlePinSubmit = async () => {
    if (pinValue.length < 4) {
      setPinError('Code PIN requis');
      return;
    }

    const filename = restoreModal.filename;
    setRestoreModal(null);
    setRestoring(filename);
    setMessage(null);

    try {
      const res = await fetch(`${API_BASE}/api/monitoring/backup/restore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ filename, pin: pinValue }),
      });
      const data = await res.json();
      setMessage({
        type: res.ok ? 'success' : 'error',
        text: data.message || data.error,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRestoring('');
    }
  };

  const closeModal = () => {
    setRestoreModal(null);
    setPinValue('');
    setPinError('');
  };

  if (loading) {
    return <BackupsSkeleton />;
  }

  const totalSizeMB = backups.reduce((acc, b) => acc + b.sizeMB, 0);

  return (
    <div className="fade-in">
      {/* Message */}
      {message && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            borderLeft: `4px solid ${message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
            padding: '12px 20px',
          }}
        >
          {message.text}
        </div>
      )}

      {/* Summary */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Total Backups</div>
          <div className="metric-value">{backups.length}</div>
          <div className="metric-label">Rétention 7 jours</div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Espace utilisé</div>
          <div className="metric-value">{totalSizeMB >= 1024 ? `${(totalSizeMB / 1024).toFixed(1)} Go` : `${totalSizeMB} Mo`}</div>
          <div className="metric-label">Tous backups cumulés</div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Prochain backup</div>
          <div className="metric-value">02:00 UTC</div>
          <div className="metric-label">Cron quotidien</div>
        </div>
      </div>

      {/* Backup list */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><HiOutlineDatabase className="icon" /> Backups disponibles</div>
          <button className="btn btn-outline" onClick={fetchBackups} style={{ padding: '6px 12px', fontSize: 12 }}>
            <HiOutlineRefresh /> Actualiser
          </button>
        </div>

        {backups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><HiOutlineDatabase /></div>
            Aucun backup disponible. Le premier backup sera créé à 02:00 UTC.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Sauvegarde</th>
                  <th>Taille</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.filename}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{formatBackupName(b.filename)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{b.filename}</div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {b.sizeMB} Mo
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ fontSize: 12, padding: '6px 14px' }}
                        disabled={!!restoring}
                        onClick={() => openRestoreModal(b.filename)}
                      >
                        {restoring === b.filename ? (
                          <><span className="spin">⟳</span> Restauration...</>
                        ) : (
                          <><HiOutlineUpload /> Restaurer en Production</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restore Confirmation Modal — Step 1: Confirm */}
      {restoreModal?.step === 'confirm' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3><HiOutlineExclamation style={{ color: 'var(--accent-red)', verticalAlign: 'middle' }} /> Restauration en Production</h3>
            <p>
              Vous êtes sur le point de restaurer le backup suivant sur la <strong>base de données de production</strong> :
            </p>
            <div style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 8, marginBottom: 16, fontFamily: 'monospace', fontSize: 13 }}>
              {formatBackupName(restoreModal.filename)}<br />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{restoreModal.filename}</span>
            </div>
            <div className="warning-text">
              ⚠ Cette action est irréversible ! La base de données actuelle sera remplacée par le contenu de ce backup. Le backend sera momentanément arrêté.
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={closeModal}>Annuler</button>
              <button className="btn btn-danger" onClick={handleConfirmStep}>Confirmer la restauration</button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal — Step 2: PIN */}
      {restoreModal?.step === 'pin' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>🔐 Vérification de sécurité</h3>
            <p>Entrez le code PIN administrateur pour confirmer la restauration :</p>
            <input
              type="password"
              className="pin-input"
              maxLength={6}
              value={pinValue}
              onChange={e => { setPinValue(e.target.value); setPinError(''); }}
              onKeyDown={e => e.key === 'Enter' && handlePinSubmit()}
              autoFocus
              placeholder="• • • •"
            />
            {pinError && <div className="pin-error">{pinError}</div>}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={closeModal}>Annuler</button>
              <button className="btn btn-danger" onClick={handlePinSubmit}>Restaurer maintenant</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
